import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { isCancel } from 'utils/axios';
import { hospitalHeaders } from '@/utils';
import type {
    CreatePatientHandoffRequest,
    PatientHandoff,
    PatientHandoffEnumsConfig,
    UpdatePatientHandoffRequest
} from '@/types/patient-handoff';
import { getErrorMessage } from 'utils/error-message';
import type { HandoffState } from './types';

export type FetchHandoffEnumsConfigArg = {
    hospitalId?: string;
};

const enumsCacheKeyForArg = (arg: FetchHandoffEnumsConfigArg | undefined) => arg?.hospitalId ?? 'default';

export const fetchHandoffEnumsConfig = createAsyncThunk<
    PatientHandoffEnumsConfig,
    FetchHandoffEnumsConfigArg | undefined,
    { rejectValue: string }
>(
    'handoff/fetchEnumsConfig',
    async (arg, { rejectWithValue, signal }) => {
        try {
            const hospitalId = arg?.hospitalId;
            const { data } = await axiosInstance.get<PatientHandoffEnumsConfig>('patient-handoffs/config/enums', {
                signal,
                ...hospitalHeaders(hospitalId)
            });
            return data;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to load handoff options'));
        }
    },
    {
        condition: (arg, { getState }) => {
            const { handoff } = getState() as { handoff: HandoffState };
            const { enumsConfig, enumsLoading, enumsCacheKey } = handoff;
            const key = enumsCacheKeyForArg(arg);
            if (enumsLoading) {
                return false;
            }
            if (enumsConfig && enumsCacheKey === key) {
                return false;
            }
            return true;
        }
    }
);

export type CreatePatientHandoffArg = {
    body: CreatePatientHandoffRequest;
    handoffType?: 'weekend' | 'general';
    hospitalId?: string;
};

/** Replace falsy string fields with `undefined` so they are omitted from the JSON body. */
const withUndefinedFalsyStrings = (
    body: CreatePatientHandoffRequest | UpdatePatientHandoffRequest | { handoffType: 'weekend' | 'general' }
) => Object.fromEntries(Object.entries(body).map(([key, value]) => [key, typeof value === 'string' && !value ? undefined : value]));

const OPTIONAL_STATUS_FIELDS = [
    'authStatus',
    'dischargePacketStatus',
    'immStatus',
    'passrStatus',
    'plannedDisposition',
    'medicallyReady'
] as const;

/** Drop optional status fields when blank so the API does not receive empty values. */
const omitEmptyStatusFields = <T extends object>(body: T): T => {
    const next = { ...body } as T & Record<(typeof OPTIONAL_STATUS_FIELDS)[number], unknown>;
    for (const key of OPTIONAL_STATUS_FIELDS) {
        const value = next[key];
        if (value == null || (typeof value === 'string' && value.trim() === '')) {
            delete next[key];
        }
    }
    return next;
};

export const createPatientHandoff = createAsyncThunk<unknown, CreatePatientHandoffArg, { rejectValue: string }>(
    'handoff/createPatientHandoff',
    async ({ body, hospitalId, handoffType }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<unknown>('patient-handoffs', withUndefinedFalsyStrings({ ...body, handoffType }), {
                ...hospitalHeaders(hospitalId)
            });
            return data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to submit handoff'));
        }
    }
);

export type FetchPatientHandoffByPatientIdArg = {
    patientId: string;
    hospitalId?: string;
    type?: 'weekend' | 'general';
};

/** @deprecated Use {@link FetchPatientHandoffByPatientIdArg} — path segment is the patient id. */
export type FetchPatientHandoffByIdArg = FetchPatientHandoffByPatientIdArg;

export type UpdatePatientHandoffArg = {
    id: string;
    body: UpdatePatientHandoffRequest;
    hospitalId?: string;
    handoffType: 'weekend' | 'general';
};

const normalizePatientHandoff = (data: unknown): PatientHandoff | null => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return null;
    }
    return data as PatientHandoff;
};

/** `GET /patient-handoffs/{patientId}` — optional `X-Hospital-Id`. */
export const fetchPatientHandoffByPatientId = createAsyncThunk<
    PatientHandoff | null,
    FetchPatientHandoffByPatientIdArg,
    { rejectValue: string }
>('handoff/fetchPatientHandoffByPatientId', async ({ patientId, hospitalId, type }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<unknown>(`patient-handoffs/${encodeURIComponent(patientId)}?type=${type || 'weekend'}`, {
            signal,
            ...hospitalHeaders(hospitalId)
        });
        return normalizePatientHandoff(data);
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to load patient handoff'));
    }
});

/** Alias for {@link fetchPatientHandoffByPatientId} (weekend handoff edit). */
export const fetchPatientHandoffById = fetchPatientHandoffByPatientId;

/** `PUT /patient-handoffs/{id}` — optional `X-Hospital-Id`. */
export const updatePatientHandoff = createAsyncThunk<unknown, UpdatePatientHandoffArg, { rejectValue: string }>(
    'handoff/updatePatientHandoff',
    async ({ id, body, hospitalId, handoffType }, { rejectWithValue, signal }) => {
        try {
            const { data } = await axiosInstance.put<unknown>(
                `patient-handoffs/${encodeURIComponent(id)}`,
                omitEmptyStatusFields({ ...body, handoffType }),
                {
                    signal,
                    ...hospitalHeaders(hospitalId)
                }
            );
            return data;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to update patient handoff'));
        }
    }
);
